// @flow

// Skipping test coverage for this component since it is not possible to unit test it correctly
/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */
/* istanbul ignore file */

/**
 * Takes an array of loadable components, and provides a component that can
 * statically stage those components if they match the current route.
 *
 * This is pretty weird, and you probably shouldn't need to use it.
 */

import * as React from 'react';
import { connect } from 'react-redux';
import { Load, Loadable } from '@ta/common.imports';

// eslint-disable-next-line no-restricted-imports
import { getStore, type State } from '@ta/common.state';
import { RouteProvider, RouteConsumer } from './RouterContext';
import match from './match';
import exact from './exact';
import type { Route, PageRoute } from './types';

type LoadableAndComponent = {| loadable: Loadable, component: React.ComponentType<*> |};

/** Takes an importer function, and produces a loadable and Load component. */
const buildLoadableAndComponent = (importer): LoadableAndComponent => {
  const loadable = new Loadable(importer);
  const LoadComponent = () => <Load component={loadable} />;
  return { loadable, component: LoadComponent };
};

/** Determines if the component applies to the current route. */
export const applyPredicates = (activeRoute: Route, candidate: PageRoute) =>
  (candidate.exact && exact(activeRoute, candidate.exact)) ||
  (candidate.match && match(activeRoute, candidate.match));

/**
 * Builds the object that hosts the components.
 */
const makeComponent = (
  routes: PageRoute[],
  loading?: React.ComponentType<{}>,
  wrapper?: React.ComponentType<{ children?: React.Node }>,
) => {
  // This ensures that child components are effectively singleton,
  // so that the Loadable instance that gets staged is the same Loadable
  // instance that gets rendered.
  const componentsForRoute = new Map();
  const getLoadableAndComponent = (route): LoadableAndComponent => {
    if (!componentsForRoute.has(route)) {
      componentsForRoute.set(route, buildLoadableAndComponent(route.component));
    }
    // $FlowFixMe: I promise this won't be null
    return componentsForRoute.get(route);
  };

  // This is the actual component that is used at render time.
  const StaticLoadableRoutes = ({ currentRoute }: { currentRoute: Route }) => {
    const children = routes
      .filter(route => applyPredicates(currentRoute, route))
      // eslint-disable-next-line react/no-array-index-key
      .map((route, key) => React.createElement(getLoadableAndComponent(route).component, { key }));

    return (
      <RouteConsumer>
        {routeState => {
          const navigate = (to, options = {}) => routeState.navigate(to, { ...options });

          return (
            <RouteProvider value={{ ...routeState, navigate }}>
              {React.createElement(wrapper || React.Fragment, null, children)}
            </RouteProvider>
          );
        }}
      </RouteConsumer>
    );
  };

  // And this does the static staging.
  StaticLoadableRoutes.stage = (...args) => {
    routes
      .filter(route => applyPredicates(getStore().getState().route, route))
      .forEach(route => {
        const loadableAndComponent = getLoadableAndComponent(route);
        if (!loadableAndComponent.loadable.isLoaded()) {
          return;
        }

        if (typeof loadableAndComponent.loadable.stage === 'function') {
          // First, stage the loadable in order to fetch translations, features, etc.
          loadableAndComponent.loadable.stage(...args);
        }

        if (typeof loadableAndComponent.loadable.get().stage === 'function') {
          // Next, stage the actual component
          loadableAndComponent.loadable.get().stage(...args);
        }
      });
  };

  StaticLoadableRoutes.ssrQueries = (state: State) => {
    const allSsrQueries = [];
    routes
      .filter(route => applyPredicates(state.route, route))
      .forEach(route => {
        const { loadable } = getLoadableAndComponent(route);
        if (!loadable.isLoaded()) {
          return;
        }

        const ssrQueries = loadable.getSsrQueries();
        if (typeof ssrQueries === 'function') {
          allSsrQueries.push(ssrQueries);
        }
      });
    return allSsrQueries.length !== 0 ? allSsrQueries.map(ssrQueries => ssrQueries(state)) : null;
  };

  return StaticLoadableRoutes;
};

/**
 * A route map that supports statically loading its children's dependencies.
 *
 * Uses Redux to pull the current route, so technically the implementation
 * detail in Router.jsx is leaking here.
 */
const ConnectedStaticLoadableRoute = (
  routes: PageRoute[],
  loading?: React.ComponentType<{}>,
  wrapper?: React.ComponentType<{ children?: React.Node }>,
) =>
  connect<Route, Route, _, _, _, _>(state => ({
    currentRoute: state.route,
  }))(makeComponent(routes, loading, wrapper));

export default ConnectedStaticLoadableRoute;
